import logger from "../utils/logger";
import { getSessionUUID } from "../components/lib/AuthToken";

// UptimeRobot API integration via backend proxy
// Backend handles API calls to avoid CORS issues

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';

export const uptimeRobotAPI = {
    // Get all monitors
    async getMonitors() {
        try {
            const sessionToken = getSessionUUID();
            const response = await fetch(`${API_BASE_URL}/api/uptimerobot/monitors`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            const data = await response.json();
            
            if (data.success) {
                return {
                    success: true,
                    monitors: data.monitors || [],
                    total: data.total || 0
                };
            } else {
                throw new Error(data.error || 'Failed to fetch monitors');
            }
        } catch (error) {
            logger.error('UptimeRobot API error:', error);
            return {
                success: false,
                error: error.message,
                monitors: []
            };
        }
    },

    // Get account details
    async getAccountDetails() {
        try {
            const sessionToken = getSessionUUID();
            const response = await fetch(`${API_BASE_URL}/api/uptimerobot/account`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            const data = await response.json();
            
            if (data.success) {
                return {
                    success: true,
                    account: data.account
                };
            } else {
                throw new Error(data.error || 'Failed to fetch account details');
            }
        } catch (error) {
            logger.error('UptimeRobot API error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    },

    // Create a new monitor
    async createMonitor(monitorData) {
        const {
            friendly_name,
            url,
            type = 1, // HTTP(s)
            interval = 300 // 5 minutes
        } = monitorData;

        try {
            const sessionToken = getSessionUUID();
            const response = await fetch(`${API_BASE_URL}/api/uptimerobot/monitors`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    friendly_name,
                    url,
                    type,
                    interval
                })
            });

            const data = await response.json();
            
            if (data.success) {
                return {
                    success: true,
                    monitor: data.monitor
                };
            } else {
                throw new Error(data.error || 'Failed to create monitor');
            }
        } catch (error) {
            logger.error('UptimeRobot API error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    },

    // Parse monitor status
    parseMonitorStatus(status) {
        const statusMap = {
            0: { text: 'Paused', color: '#6c757d', icon: '⏸️' },
            1: { text: 'Not checked yet', color: '#6c757d', icon: '⏳' },
            2: { text: 'Up', color: '#28a745', icon: '✅' },
            8: { text: 'Seems down', color: '#ffc107', icon: '⚠️' },
            9: { text: 'Down', color: '#dc3545', icon: '❌' }
        };
        return statusMap[status] || { text: 'Unknown', color: '#6c757d', icon: '❓' };
    },

    // Calculate uptime percentage - use UptimeRobot's provided ratios
    calculateUptime(monitor) {
        // Try to use UptimeRobot's all-time uptime ratio first
        if (monitor.all_time_uptime_ratio) {
            return parseFloat(monitor.all_time_uptime_ratio);
        }
        
        // Try custom uptime ratios (30 day)
        if (monitor.custom_uptime_ratios && monitor.custom_uptime_ratios.length > 0) {
            // Get the 30-day ratio (last item typically)
            const thirtyDayRatio = monitor.custom_uptime_ratios.find(ratio => ratio.range === "30") 
                || monitor.custom_uptime_ratios[monitor.custom_uptime_ratios.length - 1];
            if (thirtyDayRatio && thirtyDayRatio.ratio) {
                return parseFloat(thirtyDayRatio.ratio);
            }
        }
        
        // Fallback to log-based calculation
        const logs = monitor.logs;
        if (!logs || logs.length === 0) return 100;
        
        const totalDuration = logs.reduce((acc, log) => acc + (log.duration || 0), 0);
        const downDuration = logs
            .filter(log => log.type === 1) // Type 1 = down
            .reduce((acc, log) => acc + (log.duration || 0), 0);
        
        return totalDuration > 0 ? ((totalDuration - downDuration) / totalDuration) * 100 : 100;
    }
};

// Helper function to format duration
export const formatDuration = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};

// Helper function to format timestamp
export const formatTimestamp = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
};
